<script lang="ts">
    import {SvelteDate, SvelteMap} from "svelte/reactivity";
    import DateTime from "@/components/Inputs/DateTime.svelte";
    import CloseIcon from "../../../svelte/icons/closeIcon.svelte";
    import ErrorIcon from "../../../svelte/icons/errorIcon.svelte";
    import type {
        SetView,
        View,
        RelatedJobMap,
        employee, SchedularEmployeeAssignment, SchedularVehicles, VehicleAssignmentMap, WeekendOverride
    } from "../types";
    import { weekendsCoveredBy } from "../lib";

    import type {ValidationStoreInner} from "../validate";
    import {
        isStateJob,
        isStateNull,
        type PageStateAny,
    } from "../state";
    import { buildRelatedJobsMap, getColorForJobType, loadJob } from '../init.svelte';
    import {Button} from "@/components/ui/button";
    import { Trash2, ExternalLink, FileUp, FileCheck } from 'lucide-svelte';
    import RamsUploadModal from '@/components/vehicles/RamsUploadModal.svelte';
    import type { VehicleAssignment } from '@/types/types';
    import { Input } from '@/components/ui/input';
    import { Checkbox } from '@/components/ui/checkbox';
    import * as Select from '@/components/ui/select/index.js';
    import { formatDateLocale } from '@/lib/dateUtils';
    import { parseFieldOptions } from '@/lib/fieldUtils';
    import type { Field } from '@/types/types';

    type Props = {
        pageState: PageStateAny;
        validationStore: ValidationStoreInner;
        vehicleAssignmentMap: VehicleAssignmentMap;
        view: View;
        setView: SetView;
        vehicles: SchedularVehicles;
        relatedJobsMap: RelatedJobMap;
        employeesMap: SvelteMap<string, employee>;
        assignEmployeesContainer: HTMLDivElement | undefined;
        jobTypeField?: Field | null;
        purposeField?: Field | null;
    };

    let {
        pageState,
        validationStore,
        vehicleAssignmentMap,
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        view: _view,
        setView,
        vehicles,
        relatedJobsMap,
        assignEmployeesContainer = $bindable(),
        jobTypeField = null,
        purposeField = null,
        employeesMap,
    }: Props = $props();

    // Functions to update start and end dates
    function updateStartDate(val: Date | null) {
        if (isStateNull(pageState))
            throw new Error("tried to set the date for a job when nothing is selected");
        if (!val) return;
        const newStartDate = new SvelteDate(val);
        const item = pageState.selected?.item;

        if(item === undefined) return;

        // Calculate current end date
        const currentEndDate = item.start
            ? new Date(item.start.getTime() + item.duration * 1000)
            : null;

        // Validate: if there's an end date, make sure start is before end
        if (currentEndDate && newStartDate >= currentEndDate) {
            return; // Don't allow start to be after or equal to end
        }

        if (isStateJob(pageState)) {
            // Update start date
            item.start = newStartDate;

            // Recalculate duration if we have an end date
            if (currentEndDate) {
                item.duration = (currentEndDate.getTime() - newStartDate.getTime()) / 1000;
            }

            // Update end date to reflect new duration
            item.end = new SvelteDate(newStartDate.getTime() + item.duration * 1000);

            // Update employee assignments
            if (item.employeeAssignments) {
                for (const ea of item.employeeAssignments) {
                    ea.start_date_time = item.start;
                    ea.end_date_time = item.end;
                }
            }

            // Mark as modified
            item.modified = true;

            // Update view to show the new time
            setView({displayDate: newStartDate, scrollLeft: 250, refetchJobs: false});

        }
    }

    function updateEndDate(val: Date | null) {
        if (isStateNull(pageState) || !isStateJob(pageState)) return;
        if (!val) return;
        const newEndDate = new SvelteDate(val);
        const item = pageState.selected.item;

        // Validate: end must be after start
        if (!item.start || newEndDate <= item.start) {
            return; // Don't allow end to be before or equal to start
        }

        // Calculate new duration in seconds
        item.duration = (newEndDate.getTime() - item.start.getTime()) / 1000;

        // Update end date
        item.end = newEndDate;

        // Update employee assignments
        if (item.employeeAssignments) {
            for (const ea of item.employeeAssignments) {
                ea.end_date_time = item.end;
            }
        }

        // Mark as modified
        item.modified = true;

    }

    // const dateSetters = {
    //     get startDate() {
    //         if (isStateNull(pageState)) return undefined;
    //         if (isStateJob(pageState)) return pageState.selected?.item.start!;
    //     },
    //     set startDate(val) {
    //         updateStartDate(val);
    //     },
    //     get endDate() {
    //         if (isStateNull(pageState)) return null;
    //         const job = pageState.selected?.item;
    //         if (job.start === null) return null;
    //         const date = new Date(job.start);
    //         date.setSeconds(date.getSeconds() + job.duration);
    //         return date;
    //     },
    //     set endDate(val) {
    //         updateEndDate(val);
    //     },
    //     get durationMins() {
    //         if (isStateNull(pageState)) return null;
    //         return Math.round(pageState.selected.item?.duration / 60);
    //     },
    //     set durationMins(val) {
    //         if (isStateNull(pageState) || val === null) return;
    //         pageState.selected.item.duration = val * 60;
    //     },
    // };

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const hasErrorsFn = (val: ValidationStoreInner) => {
        return null;
    };
    let errorMessage = $derived(hasErrorsFn(validationStore));
    // const durationId = $derived(
    //     isStateNull(pageState)
    //         ? "job-null-duration"
    //         : `job-${pageState.selected.item.id}-duration`,
    // );

    let employeeAssignments = $derived.by(() => {
        return pageState.selected?.item.employeeAssignments
    });

    function getUUID(): string {
        if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
            return crypto.randomUUID();
        }
        // fallback RFC4122 v4
        return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
            const r = (Math.random() * 16) | 0;
            const v = c === 'x' ? r : (r & 0x3) | 0x8;
            return v.toString(16);
        });
    }

    function removeEmployeeAssignment(employeeAssignment: SchedularEmployeeAssignment)
    {
        if(isStateNull(pageState) || !isStateJob(pageState)) return;
        pageState.selected.item.employeeAssignments.map(ea => {
            if(ea.id === employeeAssignment.id)
            {
                ea.deleted = true;
            }
            return ea;
        });
        pageState.selected.item.modified = true;

        // Re-add the employee to the available employees list
        if (employeeAssignment.employee && employeesMap) {
            const employee = employeeAssignment.employee;
            // Only add if not already in the map
            if (!employeesMap.has(employee.id)) {
                employeesMap.set(employee.id, {
                    ...employee,
                    free_percent: 100, // Reset availability - will be recalculated on next fetch
                });
            }
        }
    }

    let titleInput: HTMLInputElement = $state();

    // Derive options arrays from passed field props using shared helper
    let jobTypes = $derived(jobTypeField ? parseFieldOptions(jobTypeField) : []);
    let purposes = $derived(purposeField ? parseFieldOptions(purposeField) : []);

    // Check if selected item is a site visit
    // Site visits have visitable_type property, vehicle assignments don't
    let isSiteVisit = $derived.by(() => {
        if (isStateNull(pageState) || !isStateJob(pageState)) return false;
        return 'visitable_type' in pageState.selected.item && pageState.selected.item.visitable_type !== undefined;
    });

    function updateJobType(newJobType: string) {
        if (isStateNull(pageState) || !isStateJob(pageState)) return;
        pageState.selected.item.job_type = newJobType;
        const newColor = getColorForJobType(newJobType);
        if (newColor) {
            pageState.selected.item.color = newColor;
        }
        pageState.selected.item.modified = true;
    }

    function updatePurpose(newPurpose: string) {
        if (isStateNull(pageState) || !isStateJob(pageState)) return;
        pageState.selected.item.purpose = newPurpose;
        pageState.selected.item.modified = true;
    }

    /**
     * Weekend anchors (YYYY-MM-DD Saturdays) the currently-selected VA spans.
     * Drives the "Weekend handling" panel. Empty array hides the panel.
     */
    let weekendAnchors = $derived.by<string[]>(() => {
        if (isStateNull(pageState) || !isStateJob(pageState)) return [];
        const item = pageState.selected.item;
        if (!item.start || !item.duration) return [];
        return weekendsCoveredBy(new Date(item.start), item.duration);
    });

    function getOverride(anchor: string): WeekendOverride {
        if (isStateNull(pageState) || !isStateJob(pageState)) {
            return { weekend_start: anchor, include_saturday: false, include_sunday: false };
        }
        const found = (pageState.selected.item.weekend_overrides ?? []).find(
            (o) => o.weekend_start === anchor,
        );
        return found ?? { weekend_start: anchor, include_saturday: false, include_sunday: false };
    }

    function setOverride(anchor: string, patch: Partial<Pick<WeekendOverride, 'include_saturday' | 'include_sunday'>>) {
        if (isStateNull(pageState) || !isStateJob(pageState)) return;
        const current = getOverride(anchor);
        const next: WeekendOverride = { ...current, ...patch };
        const others = (pageState.selected.item.weekend_overrides ?? []).filter(
            (o) => o.weekend_start !== anchor,
        );
        // Drop the entry entirely when neither flag is set so the array stays minimal.
        const updated = next.include_saturday || next.include_sunday ? [...others, next] : others;
        pageState.selected.item.weekend_overrides = updated;
        pageState.selected.item.modified = true;
    }

    function formatWeekendLabel(anchor: string): string {
        const [y, m, d] = anchor.split('-').map(Number);
        const sat = new Date(y, m - 1, d);
        const sun = new Date(sat);
        sun.setDate(sun.getDate() + 1);
        const fmt = (date: Date) => date.toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
        return `${fmt(sat)} – ${fmt(sun)}`;
    }

    let jobTypeValue = $derived.by(() => {
        if (isStateNull(pageState) || !isStateJob(pageState)) return null;
        return pageState.selected.item.job_type;
    });

    let purposeValue = $derived.by(() => {
        if (isStateNull(pageState) || !isStateJob(pageState)) return null;
        return pageState.selected.item.purpose;
    });

    let jobTypeLabel = $derived(
        jobTypes.find((option) => option.value === jobTypeValue)?.label ?? 'Select Job Type'
    );

    let purposeLabel = $derived(
        purposes.find((option) => option.value === purposeValue)?.label ?? 'Select Purpose'
    );

    // RAMS modal state
    let showRamsModal = $state(false);

    // Check if selected item has a RAMS document
    let hasRamsDocument = $derived.by(() => {
        if (isStateNull(pageState) || !isStateJob(pageState)) return false;
        const docs = pageState.selected.item.documents;
        if (!docs || docs.length === 0) return false;
        return docs.some((doc: any) => doc.title?.toLowerCase().includes('rams'));
    });

    // Convert scheduler item to VehicleAssignment type for the modal
    let selectedAsVehicleAssignment = $derived.by(() => {
        if (isStateNull(pageState) || !isStateJob(pageState)) return null;
        const item = pageState.selected.item;
        return {
            id: item.id,
            job_type: item.job_type,
            vehicle: item.vehicle,
            job: item.job,
            documents: item.documents || [],
            employeeAssignments: item.employeeAssignments,
            culmex_job_id: item.culmex_job_id,
            vehicle_id: item.vehicle_id,
            title: item.title,
            start_date_time: item.start_date_time,
            end_date_time: item.end_date_time,
            created_at: item.created_at,
            updated_at: item.updated_at,
            deleted_at: item.deleted_at
        } as VehicleAssignment;
    });

    function openRamsModal() {
        showRamsModal = true;
    }

    function closeRamsModal() {
        showRamsModal = false;
    }

    // Handle document change - update the scheduler's state
    function handleDocumentUploaded(document: any) {
        if (isStateNull(pageState) || !isStateJob(pageState)) return;

        if (document === null) {
            // Document was deleted - remove RAMS documents from the array
            if (pageState.selected.item.documents) {
                pageState.selected.item.documents = pageState.selected.item.documents.filter(
                    (doc: any) => !doc.title?.toLowerCase().includes('rams')
                );
            }
        } else {
            // Add the new document to the item's documents array
            if (!pageState.selected.item.documents) {
                pageState.selected.item.documents = [];
            }
            pageState.selected.item.documents.push(document);
        }
    }
</script>
<div class="wrapper" data-dont-unselect="true">
    <div class="top">
        {#if isStateJob(pageState)}
<!--            <Debug {pageState}/>-->
            <div class="row">
                <h3 class="subtitle w-[95%]">{pageState.selected.item.site_name} - {pageState.selected.item.vehicle?.make} - {pageState.selected.item.vehicle?.registration}</h3><button
                class="close-btn"
                onclick={() => {
                        pageState.selected = null;
                        pageState.searchStr = '';
                    }}>
                <CloseIcon clr="var(--foreground)"/>
            </button>
            </div>
            <div class="text-md grid grid-cols-1 gap-y-2">
                <div class="flex items-center justify-between">
                    <div>
                        <b>Job Start:</b> {formatDateLocale(pageState.selected.item.job?.start_date_time)} <b>Job End: </b> {formatDateLocale(pageState.selected.item.job?.end_date_time)}
                    </div>
                </div>
                {#if (pageState.selected.item as any).job_phase}
                    {@const phase = (pageState.selected.item as any).job_phase}
                    <div>
                        <span class="phase-pill">Phase {phase.sequence}</span>
                        <span class="ml-2 font-medium">{phase.label}</span>
                    </div>
                {:else if (pageState.selected.item as any).job_phase_id === null}
                    <!-- Explicit primary tag only shown when phase rows could exist on this job. -->
                    <div>
                        <span class="phase-pill phase-pill-primary">Primary phase</span>
                    </div>
                {/if}
                <div>
                    <p>
                        <b>Address:</b> {(pageState.selected.item.job as any)?.formattedAddress
                        || (pageState.selected.item.job as any)?.address?.address_line_one || 'No address'}
                    </p>
                </div>
                <div class="relative">
                    <div class="flex flex-row items-center space-x-2 ">
                        <label class="text-md font-bold  mb-1 block w-24">Title:</label>
                        <Input
                            type="text"
                            bind:value={pageState.selected.item.title}
                            placeholder="Add Title"
                            class="w-full pr-10"
                            bind:this={titleInput}
                        />
                    </div>

                </div>
                <div class="">
                    {#if isSiteVisit}
                        <div class="flex flex-row items-center space-x-2">
                        <label class="text-md font-bold  block w-24">Purpose: </label>
                        <Select.Root
                            type="single"
                            name="purpose"
                            value={purposeValue ?? undefined}
                            onValueChange={(value) => {
                                if (value) updatePurpose(value);
                            }}
                        >
                            <Select.Trigger class="w-full mx-0">
                                {purposeLabel}
                            </Select.Trigger>
                            <Select.Content class="select-dropdown">
                                <Select.Group>
                                    {#each purposes as option (option.value)}
                                        <Select.Item value={option.value} label={option.label} />
                                    {/each}
                                </Select.Group>
                            </Select.Content>
                        </Select.Root>
                        </div>
                    {:else}
                        <div class="flex flex-row items-center space-x-2">
                        <label class="text-md font-bold  block w-24">Job Type: </label>
                        <Select.Root
                            type="single"
                            name="job_type"
                            value={jobTypeValue ?? undefined}
                            onValueChange={(value) => {
                                if (value) updateJobType(value);
                            }}
                        >
                            <Select.Trigger class="w-full mx-0 {!jobTypeValue ? 'border-destructive ring-1 ring-destructive/20' : ''}">
                                {jobTypeLabel}
                            </Select.Trigger>
                            <Select.Content class="select-dropdown">
                                <Select.Group>
                                    {#each jobTypes as option (option.value)}
                                        <Select.Item value={option.value} label={option.label} />
                                    {/each}
                                </Select.Group>
                            </Select.Content>
                        </Select.Root>
                        </div>
                        {#if weekendAnchors.length > 0}
                            <div class="pt-3 space-y-2">
                                <div class="text-md font-bold">Include weekend days</div>
                                {#each weekendAnchors as anchor (anchor)}
                                    {@const override = getOverride(anchor)}
                                    <div class="flex flex-row items-center space-x-4 text-sm">
                                        <span class="w-32 shrink-0 text-muted-foreground">{formatWeekendLabel(anchor)}</span>
                                        <label class="flex items-center space-x-2">
                                            <Checkbox
                                                checked={override.include_saturday}
                                                onCheckedChange={(v) => setOverride(anchor, { include_saturday: v === true })}
                                            />
                                            <span>Sat</span>
                                        </label>
                                        <label class="flex items-center space-x-2">
                                            <Checkbox
                                                checked={override.include_sunday}
                                                onCheckedChange={(v) => setOverride(anchor, { include_sunday: v === true })}
                                            />
                                            <span>Sun</span>
                                        </label>
                                    </div>
                                {/each}
                            </div>
                        {/if}
                    {/if}
                </div>
            </div>
            <div>
                {#if pageState.selected.item.job}
                    <div class="datetime-fields grid grid-cols-2 py-2">
                        <div class="col-span-1 ">
                            <DateTime
                                value={pageState.selected.item.start ? new Date(pageState.selected.item.start) : null}
                                label="Assignment Start"
                                onValueChange={updateStartDate}
                            />
                        </div>
                        <div class="col-span-1 text-md font-bold">
                            <DateTime
                                value={pageState.selected.item.start ? new Date(pageState.selected.item.start.getTime() + pageState.selected.item.duration * 1000) : null}
                                label="Assignment Finish"
                                onValueChange={updateEndDate}
                            />
                        </div>

                    </div>
                {:else}
                    <p class="error">Opps - job No Longer Exists</p>
                {/if}
            </div>
            <div class="flex justify-between">
                <div>
                    <Button
                        onclick={() => {
                            pageState.selected.item.start = null;
                            pageState.selected.item.vehicle = null;
                            pageState.selected.item.employeeAssignments = [];
                            pageState.selected.item.modified = true;
                            pageState.selected.item.deleted = true;
                        }}
                        class="flex items-center gap-x-3 rounded-lg px-3 py-2 text-sm font-medium hover:bg-accent ">
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide-icon lucide lucide-trash w-4 h-4"><!----><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6"></path><!----><path d="M3 6h18"></path><!----><path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path><!----><!----><!----></svg>
                        Delete Job
                    </Button>
                </div>
                {#if pageState.selected.item.visitable_type?.includes('Lead')}
                    <div>
                        <Button
                            variant="outline"
                            onclick={() => window.open(`/leads/${pageState.selected.item.visitable_id}`, '_blank')}
                            class="flex items-center gap-2"
                        >
                            <ExternalLink class="w-4 h-4" />
                            View Lead
                        </Button>
                    </div>
                {:else if pageState.selected.item.job?.id}
                    <div class="flex items-center gap-2">
                        <Button
                            variant="outline"
                            onclick={() => window.open(`/jobs/${pageState.selected.item.job.id}`, '_blank')}
                            class="flex flex-1 items-center justify-center gap-2"
                        >
                            <ExternalLink class="w-4 h-4" />
                            View Job
                        </Button>
                        {#if pageState.selected.item.job?.lead_id}
                            <Button
                                variant="outline"
                                onclick={() => window.open(`/leads/${pageState.selected.item.job.lead_id}`, '_blank')}
                                class="flex flex-1 items-center justify-center gap-2"
                            >
                                <ExternalLink class="w-4 h-4" />
                                View Lead
                            </Button>
                        {/if}
                    </div>
                {/if}
                <!-- RAMS button - only show for vehicle assignments (not site visits) -->
                {#if !isSiteVisit}
                        <Button
                            variant="outline"
                            onclick={openRamsModal}
                            class="flex items-center gap-2"
                        >
                            {#if hasRamsDocument}
                                <FileCheck class="w-4 h-4 text-green-600" />
                                View RAMS
                            {:else}
                                <FileUp class="w-4 h-4" />
                                Upload RAMS
                            {/if}
                        </Button>
                {/if}
            </div>


            <hr/>
            <div class="assign-employees">
                <h4 class="subtitle">Assign Employees</h4>
                {#if pageState.selected.item.start !== null}
                <div id="assignEmployeesContainer" class="dndzone" bind:this={assignEmployeesContainer}>
                    {#if employeeAssignments && employeeAssignments.length > 0}
                        {#each Array.from(employeeAssignments.values()) as assignment (assignment.id)}
                            {#if !assignment.deleted}
                            <div class="employee-card" data-id={assignment.id} data-name={assignment.employee.name}>
                                <p>{assignment.employee.name}</p>
                                <button
                                    title="Remove Employee"
                                    onclick={() => removeEmployeeAssignment(assignment)}>
                                    <Trash2 class="w-4 h-4" />
                                </button>
                            </div>
                            {/if}
                        {/each}
                    {:else}
                        <div class="drop-zone-empty">
                            <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
                                <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/>
                                <circle cx="9" cy="7" r="4"/>
                                <path d="M22 21v-2a4 4 0 0 0-3-3.87"/>
                                <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
                            </svg>
                            <p>Drag employees here to assign</p>
                        </div>
                    {/if}
                </div>
                {:else}
                    <p class="text-md italic text-red-500">Drag Job onto Calendar to assign Employees</p>
                {/if}
            </div>
            {#if pageState.displayPopup && pageState.selected.item?.start}
                {#if errorMessage}
                    <div class="error">
                        <ErrorIcon/>
                        <p>{errorMessage}</p>
                    </div>
                {/if}
                <!--{@render scheduleFields(durationId, 1)}-->
            {/if}
        {:else}
            <h3 class="subtitle">Nothing Selected</h3>
            <p>Click on a Job to view more information about it</p>
        {/if}
    </div>

</div>

<!-- RAMS Upload Modal -->
{#if showRamsModal && selectedAsVehicleAssignment}
    <RamsUploadModal
        bind:show={showRamsModal}
        vehicleAssignment={selectedAsVehicleAssignment}
        onClose={closeRamsModal}
        onDocumentChange={handleDocumentUploaded}
    />
{/if}

<style lang="scss">
    .wrapper {
        padding: 1rem;
        display: flex;
        flex-direction: column;
        gap: 1rem;
        height: 100%;
        overflow: auto;
        color: hsl(var(--clr-on-surface-1));
    }

    .top {
        display: flex;
        flex-direction: column;
        gap: 0.5rem;
    }

    .subtitle {
        margin: 0;
        font-size: 1.15rem;
        font-weight: 600;
    }

    .row {
        display: flex;
        justify-content: space-between;
        align-items: center;
        flex-wrap: wrap;
        gap: 0.5rem;

        & > div {
            display: flex;
            gap: 0.5rem;
            align-items: center;
        }
    }

    .error {
        display: grid;
        grid-template-columns: 1.5rem 1fr;
        gap: 1rem;
        align-items: center;
        color: hsl(var(--clr-error-5));
    }

    ul.jobs-ul {
        display: flex;
        flex-direction: column;
        gap: 0.25rem;
        padding: 5px 0.25rem 5px 0.25rem;

        li {
            --sat: 80%;
            display: grid;
            grid-template-columns: 1fr max-content;
            color: var(--foreground);
            position: relative;
            background: transparent;
            z-index: 1;
            border-radius: 5px;
            padding: 0.25rem;
            outline: solid 1px hsl(var(--clr-on-surface-1));
            transition: outline 200ms;

            &::before {
                content: "";
                position: absolute;
                top: 0;
                left: 0;
                background: hsl(var(--hue) var(--sat) 80%);
                width: var(--width);
                pointer-events: none;
                z-index: -1;
                height: 100%;
            }

            &.selected {
                outline: solid 5px hsl(var(--clr-primary-5));
            }
        }

        .current {
            color: hsl(var(--clr-primary-5));
        }

        overflow-y: auto;
    }

    .category {
        --sat: 80%;
        background: hsl(var(--hue) var(--sat) 80%);
        border-radius: 5px;
        width: max-content;
        padding: 0.25rem;
    }

    .jobs-rows {
        width: 100%;

        .duration {
            --sat: 80%;
            background: hsl(var(--hue) var(--sat) 80%);
            outline: solid #333 0px;
            width: var(--width);
            height: 2rem;
        }
    }

    .phase-pill {
        display: inline-block;
        font-size: 0.7rem;
        font-weight: 600;
        line-height: 1;
        padding: 0.2rem 0.5rem;
        border-radius: 999px;
        background: hsl(var(--primary) / 0.15);
        color: hsl(var(--primary));
        border: 1px solid hsl(var(--primary) / 0.3);
        text-transform: uppercase;
        letter-spacing: 0.03em;
    }

    .phase-pill-primary {
        background: hsl(var(--muted-foreground) / 0.12);
        color: hsl(var(--muted-foreground));
        border-color: hsl(var(--muted-foreground) / 0.3);
    }

    .datetime-fields {
        gap: 0.75rem;
        position: relative;
        z-index: 1;
    }

    :global(.datetime-fields [data-slot="popover-content"]) {
        z-index: 999 !important;
    }

    .datetime-field-group {
        display: flex;
        flex-direction: column;
        gap: 0.25rem;
    }

    .datetime-label {
        font-size: 0.875rem;
        margin-bottom: 0.25rem;
    }

    .assign-employees {
        display: flex;
        flex-direction: column;
        flex: 1;
        gap: 0.5rem;

        .dndzone {
            flex: 1;
            min-height: 350px;
            overflow-y: auto;
            border: 2px dotted var(--primary);
            border-radius: 8px;
            padding: 0.25rem;
            background: hsl(var(--primary) / 0.05);
            transition: all 200ms;
            position: relative;

            &:hover {
                border-color: var(--primary);
                background: hsl(var(--primary) / 0.1);
                box-shadow: inset 0 0 20px hsl(var(--primary) / 0.1);
            }

            &:empty::before {
                content: "Drop zone ready";
                position: absolute;
                top: 50%;
                left: 50%;
                transform: translate(-50%, -50%);
                color: hsl(var(--clr-primary) / 0.5);
                font-size: 0.875rem;
                font-weight: 500;
                pointer-events: none;
            }
        }

        .drop-zone-empty {
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            gap: 1rem;
            height: 100%;
            min-height: 250px;
            color: hsl(var(--clr-primary) / 0.6);
            text-align: center;
            pointer-events: none;

            svg {
                opacity: 0.5;
            }

            p {
                margin: 0;
                font-size: 1rem;
                font-weight: 500;
            }
        }

        .employee-card {
            opacity: 0.95;
            background: hsl(120deg 60% 70%);
            color: hsl(var(--clr-on-surface-1));
            padding: 0.75rem;
            margin-bottom: 0.5rem;
            border-radius: 5px;
            border: 1px solid #00000070;
            display: flex;
            justify-content: space-between;
            align-items: center;
            transition: opacity 200ms, box-shadow 200ms, background 200ms;
            position: relative;

            &:hover {
                opacity: 1;
                box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
            }

            &:last-child {
                margin-bottom: 0;
            }

            p {
                margin: 0;
                font-size: 0.875rem;
                font-weight: 500;
            }

            button {
                display: flex;
                align-items: center;
                justify-content: center;
                padding: 0.25rem;
                background: rgba(0, 0, 0, 0.15);
                border-radius: 4px;
                border: none;
                cursor: pointer;
                transition: background 150ms, transform 150ms;

                &:hover {
                    background: rgba(0, 0, 0, 0.3);
                    transform: scale(1.1);
                }

                &:active {
                    transform: scale(0.95);
                }
            }
        }
    }
</style>
